import { Injectable } from '@angular/core';
import { resource } from '@util/resource';
import { Observable } from 'rxjs';
import { CloudinaryUploadResult } from '@interfaces/dws/cloudinary-info';
import { IntegrationEntityTypes } from '@interfaces/integration-sync-metadata';
import {
  CARD_COVER_TRANSFORMS,
  DEFAULT_DELIVERY_TRANSFORMS,
} from '@util/cloudinary-url.util';

export interface DirectUploadOptions {
  cloudName: string;
  uploadPreset: string;
  wineryId: string;
  accountId: string;
  folder?: string; // Optional folder path (defaults to store folder)
  entityId?: string; // Optional entity ID for metadata
  entityType?: IntegrationEntityTypes; // Optional entity type for metadata
}

@Injectable({
  providedIn: 'root'
})
export class CloudinaryUploadService {

  START_LINK = 'https://res.cloudinary.com/divinea/image/upload';
  CLOUDINARY_CLOUD_NAME = 'divinea';
  CLOUDINARY_UPLOAD_PRESET = 'wine-suite-temp';
  CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${this.CLOUDINARY_CLOUD_NAME}/upload`;

  /**
   * Upload image to temp folder separated by wineryId. Call moveToStore() when saving to move to final location.
   * Now uploads directly to Cloudinary to avoid backend proxy timeouts.
   *
   * @param wineryId The winery ID for folder separation in temp
   * @returns Observable with temp upload result containing publicId (use this for moveToStore)
   */
  uploadImage(file: File | Blob, wineryId: string, imageName: string, entityId?: string, entityType?: IntegrationEntityTypes): Observable<CloudinaryUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.CLOUDINARY_UPLOAD_PRESET);
    const folder = wineryId ? `production/temp/${wineryId}` : 'production/temp';
    formData.append('folder', folder);

    // Add metadata context if provided
    if (entityId || entityType) {
      const contextParts = [];
      if (wineryId) contextParts.push(`winery_id=${wineryId}`);
      if (entityId) contextParts.push(`entity_id=${entityId}`);
      if (entityType) contextParts.push(`entity_type=${entityType}`);
      if (contextParts.length > 0) {
        formData.append('context', contextParts.join('|'));
      }
    }

    // Use native fetch to bypass Angular interceptors (auth headers cause CORS issues)
    return new Observable<CloudinaryUploadResult>(observer => {
      fetch(this.CLOUDINARY_UPLOAD_URL, {
        method: 'POST',
        body: formData
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
          }
          return response.json();
        })
        .then((data: any) => {
          observer.next({
            public_id: data.public_id,
            url: data.url,
            secure_url: data.secure_url,
            width: data.width,
            height: data.height,
            format: data.format,
            resource_type: data.resource_type
          } as CloudinaryUploadResult);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Upload base64 image to temp folder separated by wineryId. Call moveToStore() when saving to move to final location.
   * Now uploads directly to Cloudinary to avoid backend proxy timeouts.
   *
   * @param wineryId The winery ID for folder separation in temp
   * @returns Observable with temp upload result containing publicId (use this for moveToStore)
   */
  uploadImageBase64(base64: string, wineryId: string, imageName: string, entityId?: string, entityType?: IntegrationEntityTypes): Observable<CloudinaryUploadResult> {
    const byteString = atob(base64.split(',')[1]);
    const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('upload_preset', this.CLOUDINARY_UPLOAD_PRESET);
    const folder = wineryId ? `production/temp/${wineryId}` : 'production/temp';
    formData.append('folder', folder);

    if (entityId || entityType) {
      const contextParts = [];
      if (wineryId) contextParts.push(`winery_id=${wineryId}`);
      if (entityId) contextParts.push(`entity_id=${entityId}`);
      if (entityType) contextParts.push(`entity_type=${entityType}`);
      if (contextParts.length > 0) {
        formData.append('context', contextParts.join('|'));
      }
    }

    // Use native fetch to bypass Angular interceptors (auth headers cause CORS issues)
    return new Observable<CloudinaryUploadResult>(observer => {
      fetch(this.CLOUDINARY_UPLOAD_URL, {
        method: 'POST',
        body: formData
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
          }
          return response.json();
        })
        .then((data: any) => {
          observer.next({
            public_id: data.public_id,
            url: data.url,
            secure_url: data.secure_url,
            width: data.width,
            height: data.height,
            format: data.format,
            resource_type: data.resource_type
          } as CloudinaryUploadResult);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Move file from temp folder to store folder.
   * Call this when the entity/form is saved after uploadImage() or uploadImageBase64().
   *
   * @param publicId The public ID from the temp upload response
   * @param wineryId The winery ID for the target folder
   * @returns Observable with the final CloudinaryUploadResult from the moved file
   */
  moveToStore(publicId: string, wineryId: string): Observable<CloudinaryUploadResult> {
    const targetFolder = `production/store/${wineryId}`;
    return resource('crm://cloudinary/move')
      .params({ publicId, targetFolder })
      .post({});
  }

  /**
   * Move multiple files from temp to store folder (batch operation).
   * Useful when multiple images are uploaded and need to be moved together on save.
   *
   * @param publicIds Array of public IDs from temp upload responses
   * @param wineryId The winery ID for the target folder
   * @returns Observable array with the final CloudinaryUploadResults
   */
  moveMultipleToStore(publicIds: string[], wineryId: string): Observable<CloudinaryUploadResult[]> {
    return new Observable<CloudinaryUploadResult[]>(observer => {
      const movePromises = publicIds.map(publicId =>
        this.moveToStore(publicId, wineryId).toPromise()
      );

      Promise.all(movePromises)
        .then(results => {
          observer.next(results);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Delivery URL for UI display — always includes f_auto/q_auto (never raw original).
   */
  getImageUrl(
    publicId: string,
    transforms: string = DEFAULT_DELIVERY_TRANSFORMS
  ): string {
    return this.getImageUrlWithTransformation(publicId, transforms);
  }

  /** Optimized cover for experience / product cards in lists. */
  getCardCoverUrl(publicId: string): string {
    return this.getImageUrlWithTransformation(publicId, CARD_COVER_TRANSFORMS);
  }

  /**
   * Raw upload URL without transforms. Prefer getImageUrl / getCardCoverUrl for UI.
   */
  getRawImageUrl(publicId: string): string {
    return `${this.START_LINK}/v1/${publicId}`;
  }

  /**
   * Returns the preview URL directly from a temp or store upload response.
   * Prefer this over getImageUrl() after uploadImage() or moveToStore()
   * to avoid reconstructing a URL that may point to the wrong folder.
   */
  getPreviewUrl(response: CloudinaryUploadResult): string {
    return response.secure_url || response.url;
  }

  getImageUrlWithTransformation(publicId: string, transformation: string): string {
    return `${this.START_LINK}/${transformation}/v1/${publicId}`;
  }

  getImageUrlWithTransformationAndSize(publicId: string, transformation: string, width: number, height: number): string {
    return `${this.START_LINK}/${transformation}/w_${width},h_${height},c_fill/v1/${publicId}`;
  }

  getImageUrlWithTransformationAndSizeAndQuality(publicId: string, transformation: string, width: number, height: number, quality: number): string { 
    return `${this.START_LINK}/${transformation}/w_${width},h_${height},q_${quality},c_fill/v1/${publicId}`;
  }

  /**
   * Upload image directly to Cloudinary (bypassing backend proxy)
   * This solves timeout issues with large files by uploading directly to Cloudinary
   * 
   * @param file File or Blob to upload
   * @param imageName Image name (will be sanitized)
   * @param options Upload options including cloudName, uploadPreset, wineryId, accountId
   * @param onProgress Optional progress callback
   * @returns Observable with Cloudinary upload result
   */
  uploadImageDirect(
    file: File | Blob,
    imageName: string,
    options: DirectUploadOptions,
    onProgress?: (progress: number) => void
  ): Observable<CloudinaryUploadResult> {
    return new Observable<CloudinaryUploadResult>(observer => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', options.uploadPreset);
      
      // Upload directly to final folder (no temp folder)
      const folder = options.folder || `production/store/${options.wineryId}`;
      formData.append('folder', folder);
      
      // Add metadata for tracking (wineryId, accountId, entityId, entityType)
      // Cloudinary supports context metadata
      let contextParts = [`winery_id=${options.wineryId}`, `account_id=${options.accountId}`];
      if (options.entityId) {
        contextParts.push(`entity_id=${options.entityId}`);
      }
      if (options.entityType) {
        contextParts.push(`entity_type=${options.entityType}`);
      }
      formData.append('context', contextParts.join('|'));

      // Optional: add public_id for better control
      const timestamp = Date.now();
      const sanitizedName = imageName.replace(/[^a-zA-Z0-9._-]/g, '_');
      const publicId = `${folder}/${timestamp}_${sanitizedName}`;
      formData.append('public_id', publicId);

      const uploadUrl = `https://api.cloudinary.com/v1_1/${options.cloudName}/image/upload`;
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            observer.next(response);
            observer.complete();
          } catch (error) {
            observer.error(new Error('Failed to parse Cloudinary response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            observer.error(new Error(errorResponse.error?.message || 'Upload failed'));
          } catch {
            observer.error(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        observer.error(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        observer.error(new Error('Upload aborted'));
      });

      xhr.open('POST', uploadUrl);
      xhr.send(formData);
    });
  }
}